import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  reorderCalendarEvents,
} from '@/api/calendars'
import type {
  CalendarEventCreate,
  CalendarEventUpdate,
  ReorderRequest,
} from '@/types'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { invalidateCalendarQueries } from '@/hooks/useCalendarQueryHelpers'

export function useCreateCalendarEvent(calendarId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CalendarEventCreate) =>
      createCalendarEvent(calendarId, payload),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient, calendarId)
      toast.success('Event created successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create event'))
    },
  })
}

export function useUpdateCalendarEvent(calendarId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      eventId,
      payload,
    }: {
      eventId: string
      payload: CalendarEventUpdate
    }) => updateCalendarEvent(calendarId, eventId, payload),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient, calendarId)
      toast.success('Event updated successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update event'))
    },
  })
}

export function useDeleteCalendarEvent(calendarId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (eventId: string) => deleteCalendarEvent(calendarId, eventId),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient, calendarId)
      toast.success('Event deleted successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete event'))
    },
  })
}

export function useReorderCalendarEvents(calendarId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReorderRequest) =>
      reorderCalendarEvents(calendarId, payload),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient, calendarId)
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to reorder events'))
    },
  })
}
